import { Link } from "react-router-dom";
import { ChevronRight, MapPin, ArrowRight } from "lucide-react";
import { SERVICES } from "@/data/services";
import { LOCATIONS, LOCATION_IMAGES } from "@/data/locations";
import { motion } from "framer-motion";

const fadeUp = { hidden: { opacity: 0, y: 24 }, visible: { opacity: 1, y: 0 } };

export const ServiceCardsSection = ({ title = "Our Electrical Services", limit = 8 }) => (
  <section className="bg-[#FAFAFA] py-14 lg:py-20" data-testid="service-cards-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between mb-8">
        <div>
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#BF8D30] mb-2 block" style={{ fontFamily: 'Outfit, sans-serif' }}>What We Do</span>
          <h2 className="text-2xl sm:text-3xl font-bold text-[#401303] tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>{title}</h2>
        </div>
        <Link to="/services" className="mt-3 sm:mt-0 text-sm font-semibold text-[#BF8D30] hover:text-[#D97904] flex items-center gap-1 transition-colors duration-200">
          View All Services <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
        {SERVICES.slice(0, limit).map((s, i) => (
          <motion.div key={s.id} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} transition={{ duration: 0.3, delay: i * 0.04 }}>
            <Link to={`/services/${s.residential.slug}`} className="block group" data-testid={`svc-card-${s.id}`}>
              <div className="rounded-2xl overflow-hidden shadow-lg group-hover:shadow-2xl group-hover:-translate-y-2 transition-all duration-300 mb-4">
                <img src={s.image} alt={`${s.title} services on the Treasure Coast`} className="w-full aspect-[4/3] object-cover" loading="lazy" />
              </div>
              <h3 className="text-sm font-bold text-[#401303] mb-1 group-hover:text-[#BF8D30] transition-colors duration-200" style={{ fontFamily: 'Outfit, sans-serif' }}>{s.title}</h3>
              <p className="text-xs text-[#6B3A1F] leading-relaxed line-clamp-2" style={{ fontFamily: 'Manrope, sans-serif' }}>{s.description}</p>
              <span className="inline-flex items-center gap-1 mt-2 text-xs font-semibold text-[#BF8D30]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Learn More <ChevronRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform duration-200" />
              </span>
            </Link>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

export const LocationCardsSection = ({ title = "Our Service Areas", limit = 12 }) => (
  <section className="bg-white py-14 lg:py-20" data-testid="location-cards-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between mb-8">
        <div>
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#317E8C] mb-2 block" style={{ fontFamily: 'Outfit, sans-serif' }}>Where We Work</span>
          <h2 className="text-2xl sm:text-3xl font-bold text-[#401303] tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>{title}</h2>
        </div>
        <Link to="/service-areas" className="mt-3 sm:mt-0 text-sm font-semibold text-[#317E8C] hover:text-[#BF8D30] flex items-center gap-1 transition-colors duration-200">
          View All Areas <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {LOCATIONS.slice(0, limit).map((loc, i) => (
          <motion.div key={loc.id} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} transition={{ duration: 0.25, delay: i * 0.03 }}>
            <Link to={`/service-areas/${loc.id}`} className="block rounded-xl overflow-hidden border border-[#6B3A1F]/10 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group" data-testid={`loc-card-${loc.id}`}>
              <div className="h-24 overflow-hidden relative">
                <img src={LOCATION_IMAGES[loc.id] || LOCATION_IMAGES["port-st-lucie"]} alt={`Electrician in ${loc.name}, FL`} className="w-full h-full object-cover object-center" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#401303]/70 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-2.5">
                  <div className="flex items-center gap-1.5">
                    <MapPin className="w-3 h-3 text-[#BF8D30] flex-shrink-0" />
                    <span className="text-white text-xs font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>{loc.name}</span>
                  </div>
                </div>
              </div>
            </Link>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);
